import { useEffect, useRef } from "react";

interface Props {
  /** The Trustoo widget data-id, e.g. "FRQXfbjwO0usxUS5FbSMbBA4cAT6qTk3gE0yGPBovQz4Og" */
  widgetId: string;
  className?: string;
}

/**
 * Lazy-loads a Trustoo review widget only when it scrolls into view, so the
 * third-party script stays off the critical render path. Mirrors the lazy-load
 * strategy used by TrustindexWidget and KvwbKeurmerkWidget.
 */
export function TrustooWidget({ widgetId, className }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let loaded = false;
    const load = () => {
      if (loaded) return;
      loaded = true;

      // The Trustoo script scans the DOM for .trustoo-widget elements and
      // hydrates them based on their data-* attributes.
      const script = document.createElement("script");
      script.src = "https://static.trustoo.nl/widget/widget_v2.js";
      script.async = true;
      el.appendChild(script);
    };

    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            load();
            io.disconnect();
          }
        },
        { rootMargin: "300px" }
      );
      io.observe(el);
      return () => io.disconnect();
    }

    // Fallback
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [widgetId]);

  return (
    <div ref={ref} className={className}>
      <div
        className="trustoo-widget"
        data-id={widgetId}
        data-language-code="nl"
        data-country-code="NL"
        data-badge="default"
        data-quote="default"
        data-size="large"
        data-type="landscape"
        data-border="hidden"
        data-theme="dark"
        data-background="transparent"
        data-google="hidden"
      />
    </div>
  );
}
